import type { Feature, Point } from 'geojson';

/** Propiedades del feature de camino en el tileset. */
export type RouteFeatureProperties = {
    /** Siempre presente: la factoria le llama .replace() sin comprobar. */
    name: string;
    route_id?: string;
    camino_id?: string;
    stroke?: string;
};
import type { Map as MapboxMap } from 'mapbox-gl';
import {dataTypes, featureTypes} from '../common/Constants';
import TrackData from './TrackData';
import {LngLat, Marker} from "mapbox-gl";
import AppService from "../common/AppService";
import MapService from "../common/MapService";

/** Item as returned by the route list endpoint. */
export interface RouteListItem {
    id: number | string;
    key?: string;
    uri?: string;
    url?: string;
    title?: string;
    name?: string;
}

/**
 * Object to store city from map marker.
 *
 * Example marker.
 * {
 id: some map id
 layer: map layer {id = "interactive-lines", type, source, source-layer}
 properties:
 - camino_id: "camino-viejo"
 - name: "Etapa 21 Congosto - Cacabelos"
 - stroke: "#aa0000"
 type: Feature
 _geometry: Point{coordinates [lon, lat]}
 }
 */

class RouteData extends TrackData {

    // Map Marker
    mapMarker?: Marker | null;

    /** Route name shown inside the marker. */
    routeName?: string;

    /** [lon, lat] of the feature this was built from. */
    firstCoordinate?: [number, number];

    /** Map feature this was built from. Solo lo rellena fromMapFeature. */
    feature?: Feature<Point, RouteFeatureProperties>;

    /**
     * Returns hotel[id]
     */
    getMapItemId(): string | undefined {
        return this.route_id;
    }

    /**
     * Create from map feature.
     */
    static fromMapFeature(feature: Feature<Point, RouteFeatureProperties>): RouteData {
        const properties = feature.properties;
        const data = new RouteData(dataTypes.route);
        data.setFeature(feature);

        const routeName = properties.name.replace('Camino:', '').trim();
        data.route_id = properties.route_id;
        data.name = properties.name;
        data.firstCoordinate = feature.geometry.coordinates as [number, number];
        data.routeName = routeName;
        data.feature = feature;

        return data;
    }

    /**
     * Create from data type, id
     */
    static fromListItem(item: RouteListItem): RouteData {
        const data = new RouteData(dataTypes.route, item.id);

        data.route_id = item.key;
        data.restUrl = item.uri;
        data.linkUrl = item.url;
        data.name = item.title ? item.title : item.name;

        return data;
    }

    /**
     * Create MAP Marker.
     *
     * @param color Hex color stroke
     */
    createMapMarker(color?: string): Marker {
        if (!this.mapMarker) {
            const el = this.createElement(color);
            this.mapMarker = new Marker(el)
                .setLngLat(this.firstCoordinate!);

            this.mapMarker.getElement().addEventListener(AppService.getInteractiveEventType(), (event) => {
                event.stopPropagation();
                if (this.feature!.geometry.type !== 'Point')
                    return;
                const coordinates = this.firstCoordinate!;
                const longLat = new LngLat(coordinates[0], coordinates[1]);
                MapService.mapComponent.handleFeatureClick(featureTypes.route, this.feature, longLat);
            });

        }
        return this.mapMarker;
    }

    /**
     * Create DOM Element
     *
     * @param color Hex color stroke
     */
    createElement(color?: string): HTMLElement {
        if (!this.htmlElement) {
            const el = document.createElement('div');
            // Ambos valores pueden ser undefined. String() hace explicita la
            // conversion que el DOM ya aplicaba. @see FeatureData.createElement
            el.id = String(this.getMapItemId());
            el.innerHTML = String(this.routeName);
            el.classList.add('route-name');
            el.style.borderColor = String(color);
            this.htmlElement = el;
        }

        return this.htmlElement;
    }


    /**
     * Add marker to Map.
     *
     * @return true if successful.
     */
    addToMap(mapObject: MapboxMap): boolean {
        const marker = this.createMapMarker();
        if (marker) {
            marker.addTo(mapObject);
            return true;
        }
        else {
            return false;
        }
    }

    /**
     * Remove marker from map.
     */
    removeMarker(): void {
        if (this.mapMarker) {
            this.mapMarker.remove();
            this.mapMarker = null;
        }
        this.removeElement();
    }

    /**
     * Remove HTML element.
     */
    removeElement(): void {
        if (this.htmlElement) {
            this.htmlElement.remove();
            this.htmlElement = null;
        }
    }

}

export default RouteData;
